/**
 * WHY a request failed, as a kind a surface can translate — never as the server's or the browser's
 * sentence (MAR-933, fix round F4).
 *
 * `apiErrorMessage` answers "what text do I show", and for a surface that has always shown the
 * server's own words that is the right question. Onboarding asks a different one: a Spanish-speaking
 * user in their first minutes must read every failure in their language, so the step needs the
 * REASON and supplies its own i18n wording for it. Server messages here are English
 * (`clientError(404, 'Client not found')`) and a request that never got an answer fails with the
 * browser's own "Failed to fetch", so reading the sentence back is not an option.
 *
 * Structured signals only, most specific first: the error body's `data.code` (the codes the server
 * actually sets), then the HTTP status, then "no status at all" for a request that got no answer.
 * Anything else is `unknown` — a caller shows an honest generic reason rather than a guessed one.
 */
export type RequestFailureKind =
  | 'network'
  | 'storage_quota'
  | 'too_large'
  | 'usage_limit'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'step_not_allowed'
  | 'unknown'

/** The `data.code`s the server sets on a refusal this module can name. */
const CODE_KINDS: Record<string, RequestFailureKind> = {
  STORAGE_QUOTA_EXCEEDED: 'storage_quota',
  FILE_TOO_LARGE: 'too_large',
  USAGE_LIMIT_REACHED: 'usage_limit',
  // MAR-978: an onboarding step the user's seat plan does not include.
  ONBOARDING_STEP_NOT_ALLOWED: 'step_not_allowed',
}

/** Error names a request that got NO answer arrives as: ofetch's wrapper, or `fetch`'s own TypeError. */
const NO_ANSWER_NAMES = new Set(['FetchError', 'TypeError'])

type FailureShape = { name?: unknown; statusCode?: unknown; status?: unknown; data?: { statusCode?: unknown; data?: { code?: unknown }; code?: unknown } }

/**
 * The HTTP status a failed request answered with, or `undefined` when it got no answer. For a caller
 * whose own endpoint gives a status one specific meaning (`setupFailureText`: a join's 404 is "this
 * invite is no longer valid") — the status, never the sentence beside it.
 */
export function requestFailureStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined
  const e = err as FailureShape
  return [e.statusCode, e.status, e.data?.statusCode].find((s): s is number => typeof s === 'number')
}

export function requestFailureKind(err: unknown): RequestFailureKind {
  if (!err || typeof err !== 'object') return 'unknown'
  const e = err as FailureShape
  const code = e.data?.data?.code ?? e.data?.code
  if (typeof code === 'string' && CODE_KINDS[code]) return CODE_KINDS[code]!

  const status = requestFailureStatus(err)
  if (status === undefined) {
    return typeof e.name === 'string' && NO_ANSWER_NAMES.has(e.name) ? 'network' : 'unknown'
  }
  if (status === 413) return 'too_large'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 429) return 'rate_limited'
  return 'unknown'
}
